import { Flex, HStack, Wrap } from '@televet/kibble-ui/build/chakra';
import { Button } from '@televet/kibble-ui/build/components/Button';
import { Icon } from '@televet/kibble-ui/build/components/Icon';
import { Menu, MenuItemProps } from '@televet/kibble-ui/build/components/Menu';
import { Tag } from '@televet/kibble-ui/build/components/Tag';
import { Text } from '@televet/kibble-ui/build/components/Text';
import React, { useCallback } from 'react';

export interface MultiSelectItem {
  id: string;
  label: string;
}

interface MultiSelectWithTagsProps {
  title: string;
  items: MultiSelectItem[];
  selectedIds: string[];
  onSelectionChange: (selectedIds: string[]) => void;
  showError?: boolean;
  errorMessage?: string;
  emptyMessage?: string;
  isLoading?: boolean;
  placeholder?: string;
  disabled?: boolean;
}

const MultiSelectWithTags: React.FC<MultiSelectWithTagsProps> = ({
  title,
  items,
  selectedIds,
  onSelectionChange,
  showError = false,
  errorMessage = 'Please select at least one item',
  emptyMessage = 'No items selected',
  isLoading = false,
  placeholder = 'Add',
  disabled = false,
}) => {
  const menuOptions = React.useMemo(
    () =>
      items
        .map((item) => ({
          value: item.id,
          label: item.label,
          isSelected: selectedIds.includes(item.id),
        }))
        .sort((a, b) => Number(b.isSelected) - Number(a.isSelected)),
    [items, selectedIds],
  );

  const handleItemSelected = useCallback(
    (allSelectedItems: MenuItemProps[]) => {
      const selectedItemIds = allSelectedItems.map((item) => item.value).filter((id) => !!id) as string[];
      onSelectionChange(selectedItemIds);
    },
    [onSelectionChange],
  );

  const handleClearAll = useCallback(() => {
    onSelectionChange([]);
  }, [onSelectionChange]);

  const handleRemoveTag = useCallback(
    (idToRemove: string) => {
      onSelectionChange(selectedIds.filter((id) => id !== idToRemove));
    },
    [onSelectionChange, selectedIds],
  );

  return (
    <Flex direction="column">
      <HStack alignItems="center" mb={2} spacing={1}>
        <Text size="md" fontWeight="bold" mr={2}>
          {title}
        </Text>
        <Menu
          buttonProps={{
            variant: 'secondarySubtle',
            leftIconName: 'plus',
            size: 'sm',
            text: placeholder,
            width: 'max-content',
            showRightIcon: false,
            sx: showError ? { 'border-color': 'red' } : {},
            disabled: disabled || isLoading,
          }}
          listProps={{
            menuItems: menuOptions,
            isSearchable: true,
            isMultiSelect: true,
            canSelectAll: true,
            onSelect: (selectedItem: MenuItemProps, allSelectedItems: MenuItemProps[] | undefined): void =>
              handleItemSelected(allSelectedItems || []),
            onSelectAll: (selectedItems: MenuItemProps[]): void => handleItemSelected(selectedItems),
          }}
        />
        <Button
          variant="ghostNeutral"
          size="sm"
          leftIcon={<Icon name="arrowCircle" size="sm" />}
          onClick={handleClearAll}
          ml={1}
          disabled={disabled || isLoading || selectedIds.length === 0}
        >
          Clear All
        </Button>
      </HStack>
      {showError ? (
        <Text size="xs" variant="danger">
          {errorMessage}
        </Text>
      ) : (
        <>
          {selectedIds.length > 0 && (
            <Wrap>
              {selectedIds.map((id) => {
                const item = items.find((item) => item.id === id);
                return (
                  <Tag
                    key={id}
                    label={item ? item.label : 'Unknown Item'}
                    rightElement={
                      <Icon name="close" size="sm" onClick={(): void => handleRemoveTag(id)} cursor="pointer" />
                    }
                  />
                );
              })}
            </Wrap>
          )}
          {selectedIds.length === 0 && (
            <Text size="sm" variant="subtle">
              {emptyMessage}
            </Text>
          )}
        </>
      )}
    </Flex>
  );
};

export default MultiSelectWithTags;
